import axios from 'axios';
import { SystemAdminApi, OrganizationAdminApi, M2MApi } from './models/m2mApi';
import { createContext, ReactNode, useContext } from 'react';
import { TpCatalogSync, TpExportAsset, TpExportData, TpOrganizationInfo, TpPluginInfo } from './models/admin';
import { UserOrganization } from './models/apis';
import { TaskExecutionType } from './models/tasks';
import { mapPythonOrganizationToOrganizationInfo } from './util';
import { useAuthenticationApi } from '../authentication/authentication';

axios.defaults.baseURL = import.meta.env.VITE_API_SERVER_URL;

const SYSTEM_ADMIN_BASE_URL = '/admin/system';
const ORGANIZATION_ADMIN_BASE_URL = '/admin/organization';

function useAdminApi(baseUrl: string): SystemAdminApi {
  const authorizationProcedures = useAuthenticationApi();
  const { getSessionId } = authorizationProcedures;

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      const error_responses = ['invalid session', 'session has expired'];
      if (error.response?.status === 403 && error_responses.includes(error.response?.data['detail'])) {
        localStorage.removeItem('sessionId');
        return getSessionId().then((headers) => {
          const config = error.config;
          config.headers['session-id'] = (headers as any)['session-id'];
          return axios.request(config);
        });
      }

      return Promise.reject(error);
    },
  );

  return {
    fetchOrganization: (organizationId) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/organizations/${organizationId}`,
          http_method: 'GET',
          request_params: { include_users: true },
        };

        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          const organization = response.data.request_response;
          return mapPythonOrganizationToOrganizationInfo(organization);
        });
      });
    },
    listOrganizations: () => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/organizations`,
          http_method: 'GET',
          request_params: { include_users: true },
        };

        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          return response.data.request_response.map((organization: any) => {
            return mapPythonOrganizationToOrganizationInfo(organization);
          });
        });
      });
    },
    createOrganization: (organizationInfo) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: '/m2m/organizations',
          http_method: 'POST',
          request_data: {
            organization_name: organizationInfo.name,
            is_active: organizationInfo.isActive,
            plugin_ids: organizationInfo.pluginIds || [],
          },
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          const newOrganization: TpOrganizationInfo = {
            identifier: response.data.request_response.identifier,
            name: response.data.request_response.organization_name,
            isActive: response.data.request_response.is_active,
            organizationPrefix: response.data.request_response.organization_prefix,
            pluginIds: response.data.request_response.plugin_ids,
          };
          if (response.data.request_response.users) {
            organizationInfo.users = response.data.request_response.users.map((user: any) => {
              return {
                email: user.email,
                organizationId: response.data.request_response.id,
                userId: user.identifier,
              };
            });
          }
          return newOrganization;
        });
      });
    },
    deleteOrganization: (organizationId: string, deleteBucket: boolean) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/organizations/${organizationId}?delete_bucket=${deleteBucket}`,
          http_method: 'DELETE',
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig);
      });
    },
    cleanupOrganization: (userInfo, organizationInfo, cleanUpInput) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };

        const requestData = {
          request_url: `/m2m/system_tasks/cleanup`,
          http_method: 'POST',
          request_data: {
            clean_templates: cleanUpInput.cleanTemplates,
            ingestion_tag: cleanUpInput.ingestionTag,
            dry_run: cleanUpInput.dryRun,
            user_email: userInfo.email,
            organization_name: organizationInfo.name,
          },
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          const taskExecution: TaskExecutionType = {
            parentAssetId: response.data.request_response.parent_asset_id,
            taskExecutionId: response.data.request_response.task_execution_id,
            taskDefinitionId: response.data.request_response.task_definition_id,
            executingParameters: response.data.request_response.executing_parameters,
            status: response.data.request_response.status.toLowerCase(),
            startTime: response.data.request_response.start_time,
            endTime: response.data.request_response.end_time,
            assets: [],
            logAvailable: response.data.request_response.log_location_available,
          };
          return taskExecution;
        });
      });
    },
    getOrganizationIngestionTags: (organizationId) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/organizations/${organizationId}/ingestion_tags`,
          http_method: 'GET',
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          return response.data.request_response;
        });
      });
    },
    listPlugins: () => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/plugins`,
          http_method: 'GET',
        };

        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          return response.data.request_response.map((plugin: any) => {
            const pluginInfo: TpPluginInfo = {
              identifier: plugin.plugin_id,
              name: plugin.plugin_name,
              prettyName: plugin.pretty_name,
            };

            return pluginInfo;
          });
        });
      });
    },
    removeOrganizationPlugin: (organizationId, pluginId) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/organizations/${organizationId}/plugins/${pluginId}`,
          http_method: 'DELETE',
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig);
      });
    },
    addOrganizationPlugin: (organizationId, pluginId) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/organizations/${organizationId}/plugins/${pluginId}`,
          http_method: 'POST',
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig);
      });
    },
    listUsers: () => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/users`,
          http_method: 'GET',
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          return response.data.request_response.map((user: any) => {
            const activeOrganization = user.organizations.find((org: any) => org.is_active);
            if (!activeOrganization) {
              throw new Error(`No active organization found for user ${user.email}`);
            }
            return {
              isOrganizationAdmin: activeOrganization.is_organization_admin,
              canWrite: activeOrganization.can_write,
              acceptedEulaVersion: user.accepted_eula_version,
              email: user.email,
              isSystemAdmin: user.is_system_admin,
              organizations: user.organizations
                .map((org: any) => {
                  const organization: UserOrganization = {
                    organizationId: org.organization_id,
                    organizationName: org.organization_name,
                    isActive: org.is_active,
                    canWrite: org.can_write,
                    isOrganizationAdmin: org.is_organization_admin,
                    userId: org.user_id,
                  };
                  return organization;
                })
                .sort((a: UserOrganization, b: UserOrganization) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0)),
            };
          });
        });
      });
    },
    createUser: (userInfo, organizationName) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/users`,
          http_method: 'POST',
          request_data: {
            email: userInfo.email,
            organization_name: organizationName,
            is_organization_admin: userInfo.isOrganizationAdmin,
            can_write: userInfo.canWrite,
          },
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          const user = response.data.request_response;
          const activeOrganization = user.organizations.find((org: any) => org.is_active);
          if (!activeOrganization) {
            throw new Error('No active organization found');
          }
          return {
            isOrganizationAdmin: activeOrganization.is_organization_admin,
            canWrite: activeOrganization.can_write,
            acceptedEulaVersion: user.accepted_eula_version,
            email: user.email,
            isSystemAdmin: user.is_system_admin,
            organizations: user.organizations
              .map((org: any) => {
                const organization: UserOrganization = {
                  organizationId: org.organization_id,
                  organizationName: org.organization_name,
                  isActive: org.is_active,
                  canWrite: org.can_write,
                  isOrganizationAdmin: org.is_organization_admin,
                };
                return organization;
              })
              .sort((a: UserOrganization, b: UserOrganization) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0)),
          };
        });
      });
    },
    modifyUser: (userInfo, activeOrganization) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/users/${userInfo.email}`,
          http_method: 'PUT',
          request_data: {
            email: userInfo.email,
            is_organization_admin: activeOrganization.isOrganizationAdmin,
            can_write: activeOrganization.canWrite,
            organization_name: activeOrganization.organizationName,
          },
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          const user = response.data.request_response;
          const activeOrganization = user.organizations.find((org: any) => org.is_active);
          if (!activeOrganization) {
            throw new Error('No active organization found');
          }
          return {
            isOrganizationAdmin: activeOrganization.is_organization_admin,
            canWrite: activeOrganization.can_write,
            acceptedEulaVersion: user.accepted_eula_version,
            email: user.email,
            isSystemAdmin: user.is_system_admin,
            organizations: user.organizations
              .map((org: any) => {
                const organization: UserOrganization = {
                  organizationId: org.organization_id,
                  organizationName: org.organization_name,
                  isActive: org.is_active,
                  canWrite: org.can_write,
                  isOrganizationAdmin: org.is_organization_admin,
                };
                return organization;
              })
              .sort((a: UserOrganization, b: UserOrganization) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0)),
          };
        });
      });
    },
    addUserToOrganization: (userInfo, userOrganization) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/users/add_user_to_organization`,
          http_method: 'POST',
          request_params: {
            user_email: userInfo.email,
            organization_name: userOrganization.organizationName,
            is_organization_admin: userOrganization.isOrganizationAdmin,
            can_write: userOrganization.canWrite,
          },
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig);
      });
    },
    removeUserFromOrganization: (userInfo, organizationName) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/users/remove_user_from_organization`,
          http_method: 'POST',
          request_params: {
            user_email: userInfo.email,
            organization_name: organizationName,
          },
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig);
      });
    },
    reassignUserActiveOrganization: (userInfo, organizationName, newActiveOrganizationId) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/users/reassign_user_active_organization`,
          http_method: 'POST',
          request_params: {
            user_email: userInfo.email,
            organization_name: organizationName,
            new_active_organization_id: newActiveOrganizationId,
          },
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig);
      });
    },
    deleteUser: (userInfo) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/users/${userInfo.email}`,
          http_method: 'DELETE',
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig);
      });
    },
    ingestData: (ingestionData) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/system_tasks/ingest_data`,
          http_method: 'POST',
          request_data: {
            organization_name: ingestionData.organizationName,
            owner_email: ingestionData.ownerEmail,
            bucket_path: ingestionData.bucketPath,
            templates_only: ingestionData.templatesOnly,
            data_only: ingestionData.dataOnly,
            allow_duplicate_ontologies: ingestionData.allowDuplicateOntologies,
            skip_data_validation: ingestionData.skipDataValidation,
            skip_failed_validations: ingestionData.skipFailedValidations,
            skip_failed_transformations: ingestionData.skipFailedTransformations,
            non_default_templates: ingestionData.nonDefaultTemplates,
            dry_run: ingestionData.dryRun,
          },
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          const taskExecution: TaskExecutionType = {
            parentAssetId: response.data.request_response.parent_asset_id,
            taskExecutionId: response.data.request_response.task_execution_id,
            taskDefinitionId: response.data.request_response.task_definition_id,
            executingParameters: response.data.request_response.executing_parameters,
            status: response.data.request_response.status.toLowerCase(),
            startTime: response.data.request_response.start_time,
            endTime: response.data.request_response.end_time,
            assets: [],
            logAvailable: response.data.request_response.log_location_available,
          };
          return taskExecution;
        });
      });
    },
    syncCatalogAsset: (catalogSyncData: TpCatalogSync) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/system_tasks/create_catalog`,
          http_method: 'POST',
          request_data: {
            organization_name: catalogSyncData.organizationName,
            user_email: catalogSyncData.userEmail,
            delete_previous_catalog_assets: catalogSyncData.deletePreviousCatalogAssets,
          },
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          const taskExecution: TaskExecutionType = {
            parentAssetId: response.data.request_response.parent_asset_id,
            taskExecutionId: response.data.request_response.task_execution_id,
            taskDefinitionId: response.data.request_response.task_definition_id,
            executingParameters: response.data.request_response.executing_parameters,
            status: response.data.request_response.status.toLowerCase(),
            startTime: response.data.request_response.start_time,
            endTime: response.data.request_response.end_time,
            assets: [],
            logAvailable: response.data.request_response.log_location_available,
          };
          return taskExecution;
        });
      });
    },
    exportAsset: (exportAssetData: TpExportAsset) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/system_tasks/export_data`,
          http_method: 'POST',
          request_data: {
            organization_name: exportAssetData.organizationName,
            user_email: exportAssetData.userEmail,
            asset_id: exportAssetData.assetId,
            related_assets: exportAssetData.relatedAssets,
          },
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          const taskExecution: TaskExecutionType = {
            parentAssetId: response.data.request_response.parent_asset_id,
            taskExecutionId: response.data.request_response.task_execution_id,
            taskDefinitionId: response.data.request_response.task_definition_id,
            executingParameters: response.data.request_response.executing_parameters,
            status: response.data.request_response.status.toLowerCase(),
            startTime: response.data.request_response.start_time,
            endTime: response.data.request_response.end_time,
            assets: [],
            logAvailable: response.data.request_response.log_location_available,
          };
          return taskExecution;
        });
      });
    },
    exportData: (exportDataInput: TpExportData) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {
          headers,
        };
        const requestData = {
          request_url: `/m2m/system_tasks/export_data`,
          http_method: 'POST',
          request_data: {
            organization_name: exportDataInput.organizationName,
            user_email: exportDataInput.userEmail,
            ingestion_tag: exportDataInput.ingestionTag,
          },
        };
        return axios.post(`${baseUrl}/actions`, requestData, requestConfig).then((response) => {
          const taskExecution: TaskExecutionType = {
            parentAssetId: response.data.request_response.parent_asset_id,
            taskExecutionId: response.data.request_response.task_execution_id,
            taskDefinitionId: response.data.request_response.task_definition_id,
            executingParameters: response.data.request_response.executing_parameters,
            status: response.data.request_response.status.toLowerCase(),
            startTime: response.data.request_response.start_time,
            endTime: response.data.request_response.end_time,
            assets: [],
            logAvailable: response.data.request_response.log_location_available,
          };
          return taskExecution;
        });
      });
    },
  };
}

function useM2MApi(): M2MApi {
  const systemAdmin: SystemAdminApi = { ...useAdminApi(SYSTEM_ADMIN_BASE_URL) };

  const orgAdminApi = useAdminApi(ORGANIZATION_ADMIN_BASE_URL);
  const organizationAdmin: OrganizationAdminApi = {
    fetchOrganization: orgAdminApi.fetchOrganization,
    cleanupOrganization: orgAdminApi.cleanupOrganization,
    getOrganizationIngestionTags: orgAdminApi.getOrganizationIngestionTags,
    listPlugins: orgAdminApi.listPlugins,
    removeOrganizationPlugin: orgAdminApi.removeOrganizationPlugin,
    addOrganizationPlugin: orgAdminApi.addOrganizationPlugin,
    ingestData: orgAdminApi.ingestData,
    syncCatalogAsset: orgAdminApi.syncCatalogAsset,
    exportAsset: orgAdminApi.exportAsset,
    exportData: orgAdminApi.exportData,
  };
  return { systemAdmin, organizationAdmin };
}

const m2MApiContext = createContext<M2MApi>({} as M2MApi);

export function M2MApiProvider({ children }: { children: ReactNode }): JSX.Element {
  return <m2MApiContext.Provider value={useM2MApi()}>{children}</m2MApiContext.Provider>;
}

export function useM2MApiContext() {
  return useContext(m2MApiContext);
}
